/**
 * Service for managing integration with the Carsharing system (C2C).
 * Provides functionality to open the C2C portal in a new tab with Planet token authentication.
 */

import axios from 'axios';

export type CarsharingEntityType = 'CUSTOMER' | 'VEHICLE' | 'HOME';

/**
 * Available sections for CUSTOMER entity (from C2C RedirectType.java)
 */
export type CarsharingCustomerSection = 
  | 'REGISTRY'
  | 'RESERVATIONS'
  | 'SUBSCRIPTIONS'
  | 'INVOICES'
  | 'SUSPENSIONS'
  | 'PAYMENTS';

/**
 * Available sections for VEHICLE entity (from C2C RedirectType.java)  
 */
export type CarsharingVehicleSection = 
  | 'MAP'
  | 'FREE_FLOATING'
  | 'OUT_OF_SERVICE'
  | 'OUT_OF_FLEET'
  | 'DISMITTED';

export type CarsharingSection = CarsharingCustomerSection | CarsharingVehicleSection;

/**
 * Available shortcut actions for VEHICLE entity (from C2C RedirectType.java)
 */
export type CarsharingShortcut = 
  | 'VEHICLE_DETAIL'           // Mostra dettaglio veicolo completo
  | 'LOG_FILE_HISTORY'         // Apre storico LogFile per il veicolo  
  | 'MOVE_HISTORY'             // Apre storico movimenti del veicolo
  | 'COMMAND_HISTORY'          // Apre storico comandi del veicolo
  | 'REPORTS_MANAGEMENT'       // Apre gestione report per il veicolo
  | 'JIRA_MANAGEMENT'          // Apre gestione Jira per il veicolo
  | 'ALARMS_WARNINGS'          // Apre allarmi/avvisi filtrati per veicolo
  | 'CUSTOMER_RESERVATIONS'    // Apre prenotazioni cliente correlate
  | 'OPERATOR_RESERVATIONS'
  | '';   // Apre prenotazioni operatore correlate

export interface CarsharingRedirectParams {
  lang: string;
  type: CarsharingEntityType;
  section?: CarsharingSection;  // Opzionale quando type è HOME
  id?: string;  // Opzionale quando type è HOME
  shortcut?: CarsharingShortcut;  // Parametro opzionale per shortcut veicoli
}

class CarsharingService {

  public async redirectToCarsharing(params: CarsharingRedirectParams): Promise<void> {
    this.validateParams(params);

    const { lang, type, section, id, shortcut } = params;

    try {
      // Call BFF to get C2C URL with authentication parameters
      const requestParams: Record<string, string> = {
        lang,
        type
      };
      
      // Includi section e id solo se non è HOME type
      if (type !== 'HOME') {
        if (section) requestParams.section = section;
        if (id) requestParams.id = id;
        if (shortcut) requestParams.shortcut = shortcut;
      }
      
      const bffResponse = await axios.get('/carsharing/redirect', {
        params: requestParams,
        headers: {
          'Accept': 'application/json'
        }
      });

      const redirectData = bffResponse.data;
      
      // Check for error response
      if (redirectData.error) {
        throw new Error(redirectData.error);
      }

      // Extract URL from BFF response (now contains auth parameters)
      const { url } = redirectData;
      if (!url) {
        throw new Error('Invalid redirect data from BFF');
      }

      console.info('Opening C2C portal with authentication parameters', { params, url });

      // Directly open the URL in new tab (no CORS issues with query parameters)
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      
      if (!newWindow) {
        console.error('Failed to open new window. Popup may be blocked.');
        throw new Error('Unable to open C2C portal. Check popup blocker settings.');
      }

      console.info('Carsharing redirect completed successfully', { params, url });
      
    } catch (error) {
      console.error('Failed to redirect to carsharing:', error);
      throw error;
    }
  }

  private validateParams(params: CarsharingRedirectParams): void {
    const { lang, type, section, id, shortcut } = params;

    if (!lang || typeof lang !== 'string') {
      throw new Error('Parameter "lang" is required and must be a string');
    }

    if (!type || !['CUSTOMER', 'VEHICLE', 'HOME'].includes(type)) {
      throw new Error('Parameter "type" must be "CUSTOMER", "VEHICLE" or "HOME"');
    }

    // Per HOME type, section e id non sono richiesti
    if (type === 'HOME') {
      // HOME non richiede section, id o shortcut
      return;
    }

    // Per CUSTOMER e VEHICLE, section e id sono obbligatori
    if (!section || typeof section !== 'string') {
      throw new Error('Parameter "section" is required and must be a string when type is not HOME');
    }

    if (!id || typeof id !== 'string') {
      throw new Error('Parameter "id" is required and must be a string when type is not HOME');
    }

    // Validate shortcut if provided
    if (shortcut !== undefined) {
      if (typeof shortcut !== 'string') {
        throw new Error('Parameter "shortcut" must be a string');
      }
      
      const validShortcuts: CarsharingShortcut[] = [
        'VEHICLE_DETAIL', 'LOG_FILE_HISTORY', 'MOVE_HISTORY', 'COMMAND_HISTORY',
        'REPORTS_MANAGEMENT', 'JIRA_MANAGEMENT', 'ALARMS_WARNINGS',
        'CUSTOMER_RESERVATIONS', 'OPERATOR_RESERVATIONS', ''
      ];
      
      if (!validShortcuts.includes(shortcut as CarsharingShortcut)) {
        throw new Error(`Parameter "shortcut" must be one of: ${validShortcuts.join(', ')}`);
      }
      
      // Shortcut currently only supported for VEHICLE type
      if (type !== 'VEHICLE') {
        throw new Error('Parameter "shortcut" is only supported for VEHICLE type');
      }
    }

    this.validateSectionForType(type, section);
  }

  private validateSectionForType(type: CarsharingEntityType, section: CarsharingSection): void {
    const customerSections: CarsharingCustomerSection[] = [
      'REGISTRY', 'RESERVATIONS', 'SUBSCRIPTIONS', 'INVOICES', 'SUSPENSIONS', 'PAYMENTS'
    ];

    const vehicleSections: CarsharingVehicleSection[] = [
      'MAP', 'FREE_FLOATING', 'OUT_OF_SERVICE', 'OUT_OF_FLEET', 'DISMITTED'
    ];

    if (type === 'CUSTOMER' && !customerSections.includes(section as CarsharingCustomerSection)) {
      throw new Error(`Section "${section}" is not valid for CUSTOMER type`);
    }

    if (type === 'VEHICLE' && !vehicleSections.includes(section as CarsharingVehicleSection)) {
      throw new Error(`Section "${section}" is not valid for VEHICLE type`);
    }
  }
}

const carsharingService = new CarsharingService();

export const redirectToCarsharing = async (params: CarsharingRedirectParams): Promise<void> => {
  return carsharingService.redirectToCarsharing(params);
};

export const useCarsharingRedirect = () => {
  return {
    redirectToCarsharing: async (params: CarsharingRedirectParams): Promise<void> => {
      return carsharingService.redirectToCarsharing(params);
    }
  };
};

export default carsharingService;
